import { Box, styled } from '@chronosphereio/chrono-ui';
import type { GaugeSeriesOption } from 'echarts';
import { useMemo } from 'react';
import { GridComponents, GridItemProps, VirtuosoGrid } from 'react-virtuoso';
import { GaugeChartBase, GaugeSeries } from '@/components';
import { UnitOptions, useFindThreshold } from '@/core';
import { GaugeChartNonNullableThresholds } from './GaugeChartPanelLoaded';
import { GAUGE_PANEL_PADDING_OFFSET } from './layout';

interface GaugeChartVirtualizedGridProps {
  gaugeData: GaugeSeries[];
  contentWidth: number;
  gaugeMinWidth: number;
  chartSize: number;
  unit: UnitOptions;
  axisLine: GaugeSeriesOption['axisLine'];
  thresholdMax: number;
  thresholds: GaugeChartNonNullableThresholds;
  hasMultipleCharts: boolean;
}

export function GaugeChartVirtualizedGrid({
  gaugeData,
  contentWidth,
  gaugeMinWidth,
  chartSize,
  unit,
  axisLine,
  thresholds,
  thresholdMax,
  hasMultipleCharts,
}: GaugeChartVirtualizedGridProps) {
  const findThreshold = useFindThreshold({ thresholds, max: thresholdMax });

  // Calculate the number of gauge chart instances that can fit per row based on panel width.
  const columnCount = Math.max(1, Math.floor(contentWidth / (gaugeMinWidth + GAUGE_PANEL_PADDING_OFFSET)));
  const itemWidth = `${100 / columnCount}%`;

  const gridComponents: GridComponents<GaugeSeries> = useMemo(
    () => ({
      List: ListContainer,
      Item: ({ children }: GridItemProps) => (
        <Box
          role="listitem"
          sx={{
            px: 1,
            width: itemWidth,
            minWidth: gaugeMinWidth,
            height: chartSize,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {children}
        </Box>
      ),
    }),
    [itemWidth, gaugeMinWidth, chartSize]
  );

  return (
    <VirtuosoGrid
      totalCount={gaugeData.length}
      components={gridComponents}
      itemContent={(index) => {
        const series = gaugeData[index];
        if (!series) return null;
        const threshold = findThreshold(series.value || 0);
        return (
          <InnerWrapper hasMultipleCharts={hasMultipleCharts}>
            <GaugeChartBase
              width={chartSize}
              height={chartSize}
              data={series}
              unit={unit}
              axisLine={axisLine}
              max={thresholdMax}
              hideValue={threshold.show_value === false}
              message={threshold.name}
            />
          </InnerWrapper>
        );
      }}
      computeItemKey={(seriesIndex) => `gauge-series-${seriesIndex}`}
      overscan={10}
    />
  );
}

const InnerWrapper = styled('div')<{ hasMultipleCharts: boolean }>(({ theme, hasMultipleCharts }) => ({
  padding: hasMultipleCharts ? theme.spacing(0.5) : 0,
}));

const ListContainer = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
});
